import {Component, Input, OnInit, DestroyRef, inject} from '@angular/core';
import {FormGroup, Validators} from '@angular/forms';
import { FormFieldBase } from '../../../interfaces/form-field-base/form-field-base';
import { MatDialog } from '@angular/material/dialog';
import { SharedFacadeService } from '@emm/shared/services/shared-facade.service';
import {MenuApiService} from '@emm/menu/services/menu-api.service';
import {TagService} from '@emm/core/services/tag.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {LanguageDialogComponent} from '@emm/shared/dialogs/language-dialog/language-dialog.component';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {formatFieldName} from '@emm/core/util/string-format.util';

@Component({
    selector: 'emm-dynamic-management-form-field',
    templateUrl: './dynamic-management-form-field.component.html',
    styleUrls: ['./dynamic-management-form-field.component.scss'],
    standalone: false
})
export class DynamicManagementFormFieldComponent<T> implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    @Input() formField!: FormFieldBase<string>;
    @Input() form!: FormGroup;
    @Input() itemTag: string;
    @Input() itemTagPath: string;
    @Input() nodeId: string;
    @Input() selectedSubmenu: string;
    @Input() selectedLocation: string;
    @Input() effectiveDate: string;
    attribute: string;
    isDarkMode: boolean;

    constructor(
        private dialog: MatDialog,
        private api: MenuApiService,
        private sharedFacadeService: SharedFacadeService,
        private tagService: TagService,
        private notificationService: NotificationService
    ) {}

    ngOnInit() {
        this.sharedFacadeService.currentTheme$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(theme => theme === 'dark-theme' ?
                this.isDarkMode = true : this.isDarkMode = false);
        this.attribute = this.formField.jsonProperty;

        // Format field name with proper spacing
        if (this.formField.name) {
            this.formField.name = formatFieldName(this.formField.name);
        }
    }

    editLocale() {
        const dialogRef = this.dialog.open(LanguageDialogComponent, {
            data: {
                attribute: this.formField,
                itemTag: this.itemTag
            },
            position: { top: '100px' },
            width: '470px'
        });
        dialogRef.afterClosed()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
                if (result !== false) {
                    this.sharedFacadeService.saveAttributeLocaleFields(result.itemTag, result.languageChanges, result.origin);
                }
        });
    }

    getInputCssClass(): string {
        let hasChanged;
        const isDropdown = (this.formField.type === 'SELECT' || this.formField.type === 'MULTI_SELECT' ||
            this.formField.type === 'BOOL');
        if (this.formField.scope !== 'LOCAL') {
            hasChanged = this.form?.controls[this.formField.jsonProperty]?.dirty;
        }
        if (this.formField.isOverride || hasChanged && isDropdown) {
            return 'attr-global-overridden';
        }
        if (this.form?.controls[this.formField.jsonProperty]?.disabled) {
            return 'attr-disabled';
        }

        return 'attr-global';
    }

    getDarkModeInputCssClass(): string {
        let hasChanged;
        if (this.formField?.scope !== 'LOCAL') {
            hasChanged = this.form?.controls[this.formField.jsonProperty]?.dirty;
        }
        if (this.formField?.isOverride || hasChanged) {
            return 'dark-attr-global-overridden';
        }
        if (this.form?.controls[this.formField.jsonProperty]?.disabled) {
            return 'dark-attr-disabled';
        }

        return 'dark-attr-global';
    }

    numberValidation(defaultValue): void {
        if (defaultValue === 'retailModifiedItemId' || defaultValue === 'subsGroupId') {
            this.rmiAndSubsGroupValidation();
        }
        if (this.form.controls[defaultValue].value
            && this.form.controls[defaultValue].value !== 'null') {

            const pattern = new RegExp('^[0-9]+(.[0-9]{1,2})?$');

            if (pattern.test(this.form.controls[defaultValue].value)) {
                this.form.controls[defaultValue].setErrors(null);
                return;
            }

            this.form.controls[defaultValue].setErrors({ pattern: true });
            this.notificationService.error('Please enter a number');
        }
    }


    imageValidation(defaultValue): void {
        if (this.form.controls[defaultValue].value
            && this.form.controls[defaultValue].value !== 'null') {

            const pattern = new RegExp(
                '^https?:\\/\\/' +                              // Match http:// or https://
                '[-a-zA-Z0-9@:%._\\+~#=]{2,256}' +              // Strict domain segment
                '\\.[a-z]{2,6}\\b' +                            // Top-level domain (e.g., .com, .net)
                '([-a-zA-Z0-9@:%_\\+.~#?&/=]*)' +               // Optional path
                '\\.(webp|png|jpg|jpeg|gif)' +                      // File extension check
                '(\\?.*)?(#.*)?$',                              // Optional query and fragment
                'i'                                             // Case-insensitive
            );


            if (pattern.test(this.form.controls[defaultValue].value)) {
                this.form.controls[defaultValue].setErrors(null);
                return;
            }

            this.form.controls[defaultValue].setErrors({ pattern: true });
            this.notificationService.error('Invalid URL format');
        }
    }

    findTagExists(): void {
        if (this.form?.controls['tag']?.value?.includes(' ')) {
            this.form?.controls['tag']?.setErrors({ incorrect: true });
            this.notificationService.error('Tags cannot contain spaces');
        }
        if (this.form?.controls['tag']?.valid) {
            if (this.form?.controls['tag']?.value === this.form?.controls['tag']?.value?.toUpperCase()) {
                this.tagService.getItemTags(this.form?.get('tag')?.value)
                    .pipe(
                        takeUntilDestroyed(this.destroyRef)
                    )
                    .subscribe({
                        next: (res) => {
                            if (res.response === true) {
                                this.form?.controls['tag']?.setErrors({ tagExists: true });
                                this.notificationService.error('Tag already exists');
                            }
                        },
                        error: (error) => {
                            this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                        }
                });
            } else {
                this.form?.controls['tag']?.setErrors({ incorrect: true });
                this.notificationService.error('Tag should be uppercase');
            }
        }
    }

    rmiAndSubsGroupValidation() {
        if (this.form?.controls) {
            const rmi = this.form.controls['retailModifiedItemId'];
            const subsGroupId = this.form.controls['subsGroupId'];

            const rmiValue = rmi?.value;
            const subsGroupIdValue = subsGroupId?.value;

            if (rmiValue && !subsGroupIdValue) {
                subsGroupId.setErrors({ pattern: true });
                subsGroupId.setValidators([Validators.required]);
                this.notificationService.error(`If an item has an RMI, it must also have a Subs Group ID`);
            } else {
                subsGroupId.setErrors(null)
                subsGroupId.clearValidators();
            }
        }
    }
}
